import { useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Loader2, CheckCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTIES = ['Brevard County', 'Flagler County', 'Lake County', 'Marion County', 'Orange County', 'Osceola County', 'Polk County', 'Putnam County', 'Seminole County', 'Sumter County', 'Volusia County'];
const CONTACT_TIMES = [
  'Morning (8AM - 12PM)',
  'Afternoon (12PM - 5PM)',
  'Evening (5PM - 8PM)',
  'Anytime'
];

export const LeadForm = ({ variant = 'default', selectedProduct = null, onSuccess, compact = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    county: '',
    email: '',
    best_time_to_contact: '',
    terms_accepted: false,
    product_id: selectedProduct?.id || null,
    product_name: selectedProduct?.name || null
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.county) newErrors.county = 'County is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.best_time_to_contact) newErrors.best_time_to_contact = 'Please select a time';
    if (!formData.terms_accepted) newErrors.terms_accepted = 'You must agree to be contacted';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      await axios.post(`${API}/leads`, formData);
      setSubmitted(true);
      toast.success('Thank you! Our team will contact you shortly.');
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error submitting lead:', err);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  if (submitted) {
    return (
      <div data-testid="lead-form-success">
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h3 className="font-bold text-xl text-gray-900 mb-2">Thank You!</h3>
          <p className="text-gray-600">
            Our installation partner will contact you immediately to schedule your installation.
          </p>
          <p className="text-sm text-gray-500 mt-4">
            You pay nothing until the job is complete.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="lead-form-container">
      <form onSubmit={handleSubmit}>
        {compact ? (
          /* Compact 2-column layout */
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            {/* Name */}
            <div>
              <Label htmlFor="name" className="text-base font-semibold text-gray-900">
                Full Name *
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`mt-1 rounded-lg h-11 text-base ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="John Smith"
                data-testid="lead-form-name"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone" className="text-base font-semibold text-gray-900">
                Phone Number *
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`mt-1 rounded-lg h-11 text-base ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="(555) 123-4567"
                data-testid="lead-form-phone"
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-base font-semibold text-gray-900">
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`mt-1 rounded-lg h-11 text-base ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="john@example.com"
                data-testid="lead-form-email"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* County */}
            <div>
              <Label htmlFor="county" className="text-base font-semibold text-gray-900">
                County *
              </Label>
              <Select
                value={formData.county}
                onValueChange={(value) => handleInputChange('county', value)}
              >
                <SelectTrigger 
                  className={`mt-1 rounded-lg h-11 text-base ${errors.county ? 'border-red-500' : 'border-gray-300'}`}
                  data-testid="lead-form-county"
                >
                  <SelectValue placeholder="Select your county" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTIES.map((county) => (
                    <SelectItem key={county} value={county} data-testid={`county-option-${county.toLowerCase().replace(' ', '-')}`}>
                      {county}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.county && <p className="text-red-500 text-xs mt-1">{errors.county}</p>}
            </div>

            {/* Address - Full Width */}
            <div className="sm:col-span-2">
              <Label htmlFor="address" className="text-base font-semibold text-gray-900">
                Installation Address *
              </Label>
              <Input
                id="address"
                type="text"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className={`mt-1 rounded-lg h-11 text-base ${errors.address ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="123 Main St, City, FL 32901"
                data-testid="lead-form-address"
              />
              {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
            </div>

            {/* Best Time to Contact */}
            <div className="sm:col-span-2">
              <Label htmlFor="contact_time" className="text-base font-semibold text-gray-900">
                Best Time to Contact *
              </Label>
              <Select
                value={formData.best_time_to_contact}
                onValueChange={(value) => handleInputChange('best_time_to_contact', value)}
              >
                <SelectTrigger 
                  className={`mt-1 rounded-lg h-11 text-base ${errors.best_time_to_contact ? 'border-red-500' : 'border-gray-300'}`}
                  data-testid="lead-form-contact-time"
                >
                  <SelectValue placeholder="Select preferred time" />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_TIMES.map((time) => (
                    <SelectItem key={time} value={time} data-testid={`time-option-${time.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.best_time_to_contact && <p className="text-red-500 text-xs mt-1">{errors.best_time_to_contact}</p>}
            </div>

            {/* Terms & Conditions - Full Width */}
            <div className="sm:col-span-2 flex items-start gap-3 pt-2">
              <Checkbox
                id="terms"
                checked={formData.terms_accepted}
                onCheckedChange={(checked) => handleInputChange('terms_accepted', checked)}
                className="mt-0.5"
                data-testid="lead-form-terms"
              />
              <Label htmlFor="terms" className="text-sm text-gray-700 leading-tight">
                I Agree to have either Appliance Direct or an Installation Partner contact me.
              </Label>
            </div>
            {errors.terms_accepted && <p className="sm:col-span-2 text-red-500 text-xs">{errors.terms_accepted}</p>}

            {/* Submit Button - Full Width */}
            <div className="sm:col-span-2 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-6 py-3.5 font-bold rounded-lg transition-colors flex items-center justify-center gap-2 text-lg"
                data-testid="lead-form-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Schedule Install
                  </>
                )}
              </button>
              <p className="text-xs text-center text-gray-500 pt-3">
                Our installation partner will contact you immediately. 
                <strong> You pay nothing until the job is complete.</strong>
              </p>
            </div>
          </div>
        ) : (
          /* Standard single-column layout */
          <div className="space-y-4">
            {/* Name */}
            <div>
              <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                Full Name *
              </Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`mt-1.5 rounded-lg ${errors.name ? 'border-red-500' : 'border-gray-200'}`}
                placeholder="John Smith"
                data-testid="lead-form-name"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                Phone Number *
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`mt-1.5 rounded-lg ${errors.phone ? 'border-red-500' : 'border-gray-200'}`}
                placeholder="(555) 123-4567"
                data-testid="lead-form-phone"
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`mt-1.5 rounded-lg ${errors.email ? 'border-red-500' : 'border-gray-200'}`}
                placeholder="john@example.com"
                data-testid="lead-form-email"
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Address */}
            <div>
              <Label htmlFor="address" className="text-sm font-medium text-gray-700">
                Installation Address *
              </Label>
              <Input
                id="address"
                type="text"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className={`mt-1.5 rounded-lg ${errors.address ? 'border-red-500' : 'border-gray-200'}`}
                placeholder="123 Main St, City, FL 32901"
                data-testid="lead-form-address"
              />
              {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
            </div>

            {/* County */}
            <div>
              <Label htmlFor="county" className="text-sm font-medium text-gray-700">
                County *
              </Label>
              <Select
                value={formData.county}
                onValueChange={(value) => handleInputChange('county', value)}
              >
                <SelectTrigger 
                  className={`mt-1.5 rounded-lg ${errors.county ? 'border-red-500' : 'border-gray-200'}`}
                  data-testid="lead-form-county"
                >
                  <SelectValue placeholder="Select your county" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTIES.map((county) => (
                    <SelectItem key={county} value={county} data-testid={`county-option-${county.toLowerCase().replace(' ', '-')}`}>
                      {county}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.county && <p className="text-red-500 text-xs mt-1">{errors.county}</p>}
            </div>

            {/* Best Time to Contact */}
            <div>
              <Label htmlFor="contact_time" className="text-sm font-medium text-gray-700">
                Best Time to Contact *
              </Label>
              <Select
                value={formData.best_time_to_contact}
                onValueChange={(value) => handleInputChange('best_time_to_contact', value)}
              >
                <SelectTrigger 
                  className={`mt-1.5 rounded-lg ${errors.best_time_to_contact ? 'border-red-500' : 'border-gray-200'}`}
                  data-testid="lead-form-contact-time"
                >
                  <SelectValue placeholder="Select preferred time" />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_TIMES.map((time) => (
                    <SelectItem key={time} value={time} data-testid={`time-option-${time.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                      {time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.best_time_to_contact && <p className="text-red-500 text-xs mt-1">{errors.best_time_to_contact}</p>}
            </div>

            {/* Terms & Conditions */}
            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                id="terms"
                checked={formData.terms_accepted}
                onCheckedChange={(checked) => handleInputChange('terms_accepted', checked)}
                className="mt-0.5"
                data-testid="lead-form-terms"
              />
              <Label htmlFor="terms" className="text-sm text-gray-600 leading-tight">
                I Agree to have either Appliance Direct or an Installation Partner contact me.
              </Label>
            </div>
            {errors.terms_accepted && <p className="text-red-500 text-xs">{errors.terms_accepted}</p>}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white px-6 py-3 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 mt-6"
              data-testid="lead-form-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Request Free Estimate
                </>
              )}
            </button>

            <p className="text-xs text-center text-gray-500 pt-2">
              Our installation partner will contact you immediately. 
              <strong> You pay nothing until the job is complete.</strong>
            </p>
          </div>
        )}
      </form>
    </div>
  );
};

export default LeadForm;
